import React, { useState, useMemo, useRef, useCallback } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
} from "recharts";

/* ─────────────────────────── THEME ─────────────────────────── */
const GR = "#10b981",
  RD = "#ef4444",
  AM = "#f59e0b",
  BL = "#3b82f6",
  CY = "#06b6d4",
  PR = "#8b5cf6";

const SCOL = {
  Banking: "#3b82f6",
  Finance: "#6366f1",
  Insurance: "#8b5cf6",
  IT: "#06b6d4",
  "IT/Telecom": "#0ea5e9",
  Technology: "#14b8a6",
  Power: "#f59e0b",
  "Capital Goods": "#f97316",
  Defence: "#ef4444",
  Infrastructure: "#a78bfa",
  Auto: "#22c55e",
  Pharma: "#ec4899",
  Healthcare: "#f43f5e",
  Metals: "#94a3b8",
  Textiles: "#84cc16",
  FMCG: "#10b981",
  Chemicals: "#22d3ee",
  Agrochemicals: "#65a30d",
  "Real Estate": "#fb923c",
  Telecom: "#6366f1",
  Media: "#a855f7",
  Sugar: "#fb923c",
  Paper: "#78716c",
  Hotels: "#d946ef",
  Retail: "#0891b2",
  Logistics: "#059669",
  Industrials: "#475569",
  "NSE Listed": "#475569",
  "Consumer Goods": "#16a34a",
  Mining: "#64748b",
  "Oil & Gas": "#92400e",
  Cement: "#a16207",
  Paints: "#0f766e",
  Conglomerate: "#7c3aed",
  Plastics: "#0369a1",
  Jewellery: "#b45309",
  Entertainment: "#c026d3",
};
const SEMO = {
  Banking: "🏦",
  Finance: "🏛️",
  Insurance: "🛡️",
  IT: "💻",
  "IT/Telecom": "📡",
  Technology: "⚙️",
  Power: "⚡",
  "Capital Goods": "🔧",
  Defence: "🛡️",
  Infrastructure: "🏗️",
  Auto: "🚗",
  Pharma: "💊",
  Healthcare: "🏥",
  Metals: "⚙️",
  Textiles: "🧵",
  FMCG: "🛒",
  Chemicals: "🧪",
  Agrochemicals: "🌱",
  "Real Estate": "🏢",
  Telecom: "📡",
  Media: "📺",
  Sugar: "🍬",
  Paper: "📄",
  Hotels: "🏨",
  Retail: "🛍️",
  Logistics: "🚚",
  Industrials: "🏭",
};

const fi = (n, d = 0) =>
  Number(n).toLocaleString("en-IN", { maximumFractionDigits: d });
const mono = (v) => ({ fontFamily: "'Space Mono',monospace", ...v });

/* ─────────────────────────── SEEDED RNG ──────────────────────── */
function mkRng(seed) {
  let s = seed >>> 0;
  return () => {
    s ^= s << 13;
    s ^= s >> 17;
    s ^= s << 5;
    return (s >>> 0) / 0xffffffff;
  };
}

/* ══════════════════════════════════════════════════════════════════
   EXACT JS PORTS OF mcmc_nse_full.py ALGORITHM FUNCTIONS
   Root cause of old mismatch: JS used Camarilla from 120-day H/L
   (levels 20-40% from CMP). Python uses PDH/PDL from yesterday
   (levels 0.5-2% from CMP). This version fixes that completely.
══════════════════════════════════════════════════════════════════ */

/**
 * compute_cpr — Central Pivot Range + PDH/PDL + 20-day Swing Fibonacci
 * EXACT port of Python compute_cpr(high, low, close).
 *
 * hs, ls, cs = arrays of daily high / low / close prices
 * Uses last bar as "previous day" for PDH/PDL/PDC.
 * 5/10/20-day swings for Fibonacci retracements.
 */
function computeCPR(hs, ls, cs) {
  const n = cs.length;
  // Previous-day bar (last in array — same as Python high[-1])
  const pdh = hs[n - 1],
    pdl = ls[n - 1],
    pdc = cs[n - 1];

  const bc = (pdh + pdl) / 2;
  const pivot = (pdh + pdl + pdc) / 3;
  const tc = pivot - bc + pivot;
  const width = tc - bc;
  const wp = (width / Math.max(pdc, 1)) * 100;
  const cpr_type =
    wp < 0.4
      ? "Narrow → Trending"
      : wp < 1.0
      ? "Normal → Standard"
      : "Wide → Range-bound";

  const n5 = Math.min(5, n),
    n10 = Math.min(10, n),
    n20 = Math.min(20, n);
  const sh5 = Math.max(...hs.slice(-n5)),
    sl5 = Math.min(...ls.slice(-n5));
  const sh10 = Math.max(...hs.slice(-n10)),
    sl10 = Math.min(...ls.slice(-n10));
  const sh20 = Math.max(...hs.slice(-n20)),
    sl20 = Math.min(...ls.slice(-n20));
  const fd = sh20 - sl20;

  return {
    pivot: +pivot.toFixed(2),
    tc: +tc.toFixed(2),
    bc: +bc.toFixed(2),
    width: +width.toFixed(2),
    width_pct: +wp.toFixed(3),
    cpr_type,
    pdh: +pdh.toFixed(2),
    pdl: +pdl.toFixed(2),
    pdc: +pdc.toFixed(2),
    swing_h5: +sh5.toFixed(2),
    swing_l5: +sl5.toFixed(2),
    swing_h10: +sh10.toFixed(2),
    swing_l10: +sl10.toFixed(2),
    swing_h20: +sh20.toFixed(2),
    swing_l20: +sl20.toFixed(2),
    fib_786: +(sh20 - 0.786 * fd).toFixed(2),
    fib_618: +(sh20 - 0.618 * fd).toFixed(2),
    fib_500: +(sh20 - 0.5 * fd).toFixed(2),
    fib_382: +(sh20 - 0.382 * fd).toFixed(2),
    fib_236: +(sh20 - 0.236 * fd).toFixed(2),
    fib_ext1: +(sh20 + 0.272 * fd).toFixed(2),
  };
}

/**
 * compute_supertrend — Exact port of Python compute_supertrend().
 * period=10, multiplier=3.0. Wilder EMA for ATR.
 */
function computeST(hs, ls, cs, period = 10, mult = 3.0) {
  const n = cs.length;
  if (n < period + 5) {
    const c = cs[n - 1];
    return {
      direction: 1,
      level: c,
      upper: c,
      lower: c,
      strength: 0.3,
      atr_used: c * 0.015,
    };
  }
  const tr = [];
  for (let i = 1; i < n; i++)
    tr.push(
      Math.max(
        hs[i] - ls[i],
        Math.abs(hs[i] - cs[i - 1]),
        Math.abs(ls[i] - cs[i - 1])
      )
    );
  const al = 2 / (period + 1);
  const atr = [tr[0]];
  for (let i = 1; i < tr.length; i++)
    atr.push(tr[i] * al + atr[i - 1] * (1 - al));
  const ub = [],
    lb = [];
  for (let i = 0; i < atr.length; i++) {
    const m = (hs[i + 1] + ls[i + 1]) / 2;
    ub.push(m + mult * atr[i]);
    lb.push(m - mult * atr[i]);
  }
  const fub = [...ub],
    flb = [...lb],
    dir = new Array(ub.length).fill(1);
  for (let i = 1; i < ub.length; i++) {
    fub[i] = ub[i] < fub[i - 1] || cs[i + 1] > fub[i - 1] ? ub[i] : fub[i - 1];
    flb[i] = lb[i] > flb[i - 1] || cs[i + 1] < flb[i - 1] ? lb[i] : flb[i - 1];
    if (dir[i - 1] === -1 && cs[i + 1] > fub[i - 1]) dir[i] = 1;
    else if (dir[i - 1] === 1 && cs[i + 1] < flb[i - 1]) dir[i] = -1;
    else dir[i] = dir[i - 1];
  }
  const li = ub.length - 1,
    d = dir[li];
  const level = d === 1 ? flb[li] : fub[li],
    la = atr[li];
  return {
    direction: d,
    level: +level.toFixed(2),
    upper: +fub[li].toFixed(2),
    lower: +flb[li].toFixed(2),
    strength: +Math.min(5, Math.abs(cs[n - 1] - level) / (la + 1e-9)).toFixed(
      3
    ),
    atr_used: +la.toFixed(2),
  };
}

/**
 * Black-Scholes — exact port of Python bs_greeks(). r=6.7% (India Gsec)
 */
function bsG(S, K, Td, iv, r = 0.067) {
  try {
    const T = Td / 365;
    if (T <= 0 || iv <= 0 || S <= 0 || K <= 0) throw 0;
    const d1 =
        (Math.log(S / K) + (r + 0.5 * iv * iv) * T) / (iv * Math.sqrt(T)),
      d2 = d1 - iv * Math.sqrt(T);
    const Nc = (x) => {
      const ax = Math.abs(x),
        t = 1 / (1 + 0.2316419 * ax);
      const p =
        t *
        (0.31938153 +
          t *
            (-0.356563782 +
              t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
      const f = 1 - (Math.exp(-0.5 * ax * ax) / Math.sqrt(2 * Math.PI)) * p;
      return x >= 0 ? f : 1 - f;
    };
    const pd = (x) => Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
    const call = S * Nc(d1) - K * Math.exp(-r * T) * Nc(d2);
    const put = K * Math.exp(-r * T) * Nc(-d2) - S * Nc(-d1);
    const dc = Nc(d1),
      gm = pd(d1) / (S * iv * Math.sqrt(T));
    const vg = (S * pd(d1) * Math.sqrt(T)) / 100;
    const tc =
      (-(S * pd(d1) * iv) / (2 * Math.sqrt(T)) -
        r * K * Math.exp(-r * T) * Nc(d2)) /
      365;
    return {
      call: +call.toFixed(2),
      put: +put.toFixed(2),
      delta_c: +dc.toFixed(4),
      delta_p: +(dc - 1).toFixed(4),
      gamma: +gm.toFixed(6),
      vega: +vg.toFixed(4),
      theta_c: +tc.toFixed(4),
      iv_pct: +(iv * 100).toFixed(2),
      atm: K,
    };
  } catch {
    return {
      call: 0,
      put: 0,
      delta_c: 0.5,
      delta_p: -0.5,
      gamma: 0,
      vega: 0,
      theta_c: 0,
      iv_pct: 0,
      atm: K,
    };
  }
}

/**
 * generate_signals — EXACT JS port of Python Section 9.
 *
 * Uses CPR zones (PDH/PDL/TC/BC) + Swing Fibonacci — NOT Camarilla.
 * This is what gives trade levels within 1-3% of CMP (market-realistic).
 *
 * Zone 1: above PDH  → PDH Breakout Buy
 * Zone 2: inside CPR → CPR Support Buy
 * Zone 3: PDL<CMP<BC → Fib 618 Pullback Buy
 * Zone 4: below PDL  → PDL Breakdown Short
 * Min R:R enforced at 1.5 — target scaled outward if needed.
 */
function generateSignals(cmp, mcmc, garch, regime, sim, tech, cpr, st, lot) {
  const mu = mcmc.mu ?? 0.1,
    sigma = mcmc.sigma ?? 0.25;
  const prob_up = sim.prob_up ?? 0.5;
  const rsi = tech.rsi14 ?? 50,
    macd_h = tech.macd_hist ?? 0;
  const obv_tr = tech.obv_trend ?? 0,
    stoch = tech.stoch_k ?? 50;
  const vol_r = tech.vol_ratio ?? 1.0,
    golden = tech.golden_cross ?? false;
  const atr = tech.atr14 ?? cmp * 0.015; // real ATR14 only — no sigma blend
  const garch_v = garch.cond_vol_annual ?? sigma;
  const in_bull = regime.current === "Bull";
  const sw_prob = regime.switch_prob ?? 0.3;
  const reg_cur = regime.current ?? "Unknown";
  const st_dir = st.direction ?? 1;

  // CPR levels (tight — yesterday's H/L/C based)
  const tc = cpr.tc ?? cmp + atr * 0.3;
  const bc = cpr.bc ?? cmp - atr * 0.3;
  const pivot = cpr.pivot ?? cmp;
  const pdh = cpr.pdh ?? cmp + atr * 0.8;
  const pdl = cpr.pdl ?? cmp - atr * 0.8;
  const cpr_w = Math.max(tc - bc, atr * 0.1);
  const f618 = cpr.fib_618 ?? cmp - atr * 0.5;
  const f382 = cpr.fib_382 ?? cmp + atr * 0.5;
  const f236 = cpr.fib_236 ?? cmp + atr * 1.0;
  const fext1 = cpr.fib_ext1 ?? cmp + atr * 1.5;
  const f786 = cpr.fib_786 ?? cmp - atr * 0.8;
  const sl20 = cpr.swing_l20 ?? cmp - atr * 2.0;

  // ── Score (12 components, identical to Python) ─────────────────────
  const sc = {
    prob_up: prob_up * 28,
    mu_drift: Math.min(mu / 0.3, 1) * 18,
    low_vol: (1 - Math.min(sigma / 0.5, 1)) * 12,
    rsi: rsi < 35 ? 10 : rsi < 45 ? 7 : rsi < 55 ? 4 : rsi < 65 ? 1 : 0,
    macd: macd_h > 0 ? 7 : 0,
    obv: obv_tr > 0 ? 5 : 0,
    regime: in_bull ? 8 : reg_cur === "Transitioning" ? 3 : 0,
    stoch: stoch < 30 ? 4 : stoch < 45 ? 2 : 0,
    golden: golden ? 5 : 0,
    supertrend: st_dir === 1 ? 6 : 0,
    vol_surge: vol_r > 1.5 && macd_h > 0 ? 3 : 0,
    sw_penalty: sw_prob > 0.6 ? -5 : 0,
  };
  const score = Math.min(
    97,
    Math.max(5, Math.round(Object.values(sc).reduce((a, b) => a + b, 0)))
  );
  const verdict =
    score >= 75
      ? "Strong Buy"
      : score >= 60
      ? "Buy"
      : score >= 45
      ? "Hold"
      : score >= 30
      ? "Caution"
      : "Avoid";
  const vcol = {
    "Strong Buy": GR,
    Buy: "#22c55e",
    Hold: AM,
    Caution: RD,
    Avoid: "#dc2626",
  }[verdict];

  // ── CPR zone detection (same as Python) ───────────────────────────
  const above_pdh = cmp >= pdh;
  const in_cpr = bc <= cmp && cmp <= tc;
  const below_pdl = cmp <= pdl;
  const pullback = pdl < cmp && cmp < bc;

  const MIN_RR = 1.5;
  let entry_l, tgt_l, sl_l, trade_type;

  if (above_pdh) {
    entry_l = +(pdh + 0.05 * atr).toFixed(2);
    tgt_l = +Math.min(fext1, pdh + 2.0 * atr).toFixed(2);
    sl_l = +(pdh - 0.3 * atr).toFixed(2);
    trade_type = "PDH Breakout Buy";
  } else if (in_cpr) {
    entry_l = +bc.toFixed(2);
    tgt_l = +(tc + 0.5 * cpr_w).toFixed(2);
    sl_l = +(bc - 0.5 * atr).toFixed(2);
    trade_type = "CPR Support Buy";
  } else if (pullback) {
    entry_l = +Math.max(f618, pdl + 0.1 * atr).toFixed(2);
    tgt_l = +Math.min(Math.max(f382, tc), entry_l + 2.0 * atr).toFixed(2);
    sl_l = +(pdl - 0.2 * atr).toFixed(2);
    trade_type = "Fib 618 Pullback Buy";
  } else {
    // Below PDL — short bias shown as long levels (PDL-based)
    entry_l = +(pdl - 0.1 * atr).toFixed(2);
    tgt_l = +Math.max(f786, sl20 + 0.2 * atr).toFixed(2);
    sl_l = +(pdl + 0.4 * atr).toFixed(2);
    trade_type = "PDL Breakdown Short";
  }

  // Enforce min R:R=1.5 — scale target if needed (same as Python)
  let risk = Math.abs(entry_l - sl_l);
  let gain = Math.abs(tgt_l - entry_l);
  if (risk < 0.01) {
    risk = atr * 0.5;
    sl_l = +(entry_l - risk).toFixed(2);
  }
  if (gain < MIN_RR * risk) {
    if (trade_type.includes("Short"))
      tgt_l = +(entry_l - MIN_RR * risk).toFixed(2);
    else tgt_l = +(entry_l + MIN_RR * risk).toFixed(2);
    trade_type += " [target extended]";
  }
  const rr_l = +(
    Math.abs(tgt_l - entry_l) / Math.max(Math.abs(entry_l - sl_l), 0.01)
  ).toFixed(2);

  // ── Short levels ──────────────────────────────────────────────────
  let entry_s, tgt_s, sl_s;
  if (above_pdh) {
    entry_s = +(pdh + 0.1 * atr).toFixed(2);
    tgt_s = +Math.max(bc, f618).toFixed(2);
    sl_s = +(pdh + 0.6 * atr).toFixed(2);
  } else if (in_cpr) {
    entry_s = +tc.toFixed(2);
    tgt_s = +Math.max(pdl, bc - cpr_w).toFixed(2);
    sl_s = +(tc + 0.5 * atr).toFixed(2);
  } else {
    entry_s = +(bc + 0.1 * atr).toFixed(2);
    tgt_s = +Math.max(pdl, f618).toFixed(2);
    sl_s = +(pivot + 0.3 * atr).toFixed(2);
  }
  const rr_s = +(
    Math.abs(entry_s - tgt_s) / Math.max(Math.abs(sl_s - entry_s), 0.01)
  ).toFixed(2);

  // ── Options ───────────────────────────────────────────────────────
  const iv_adj = 0.55 * sigma + 0.45 * garch_v;
  const atm = Math.round(cmp / 50) * 50;
  const g30 = bsG(cmp, atm, 30, iv_adj),
    g60 = bsG(cmp, atm, 60, iv_adj),
    g90 = bsG(cmp, atm, 90, iv_adj);

  let opt;
  if (mu > 0.2 && in_bull && st_dir === 1)
    opt = "BUY CALLS (strong bull + Supertrend up)";
  else if (mu > 0.12 && prob_up > 0.58) opt = "BULL CALL SPREAD";
  else if (mu < -0.05 || (sw_prob > 0.65 && st_dir === -1))
    opt = "BUY PUTS (bear signal)";
  else if (sigma > 0.38 || garch_v > 0.42)
    opt = "BUY STRADDLE (high volatility)";
  else if (sw_prob < 0.25 && reg_cur !== "Bull")
    opt = "IRON CONDOR (range bound)";
  else opt = "WAIT FOR CLEARER SIGNAL";

  return {
    score,
    score_breakdown: sc,
    verdict,
    verdict_color: vcol,
    trade_type,
    regime_signal: `${reg_cur} · switch_risk=${(sw_prob * 100).toFixed(0)}%`,
    supertrend_dir: st_dir === 1 ? "Bullish ▲" : "Bearish ▼",
    supertrend_level: st.level ?? cmp,
    supertrend_strength: st.strength ?? 0,
    garch_vol_annual: +(garch_v * 100).toFixed(1),
    eff_vol_annual: +(iv_adj * 100).toFixed(1),
    // Long trade (CPR anchored — levels within 1-3% of CMP)
    entry_long: entry_l,
    target_long: tgt_l,
    sl_long: sl_l,
    rr_long: rr_l,
    // Short trade
    entry_short: entry_s,
    target_short: tgt_s,
    sl_short: sl_s,
    rr_short: rr_s,
    daily_move_rs: +atr.toFixed(2),
    daily_move_pct: +((atr / cmp) * 100).toFixed(3),
    // CPR display levels (flat in signals, matches Python output schema)
    cpr_tc: +tc.toFixed(2),
    cpr_bc: +bc.toFixed(2),
    cpr_pivot: +pivot.toFixed(2),
    cpr_width: +cpr_w.toFixed(2),
    cpr_type: cpr.cpr_type ?? "",
    pdh: +pdh.toFixed(2),
    pdl: +pdl.toFixed(2),
    fib_618: +f618.toFixed(2),
    fib_382: +f382.toFixed(2),
    fib_236: +f236.toFixed(2),
    fib_ext1: +fext1.toFixed(2),
    // Options
    iv: +(iv_adj * 100).toFixed(1),
    atm,
    greeks_30d: g30,
    greeks_60d: g60,
    greeks_90d: g90,
    opt_signal: opt,
    bull_target_6m: sim.bull_scenario_p50 ?? Math.round(cmp * 1.12),
    bear_target_6m: sim.bear_scenario_p50 ?? Math.round(cmp * 0.9),
    max_loss_1lot_30d: Math.round(g30.call * lot),
    max_gain_est_1lot: Math.round(Math.abs(tgt_l - entry_l) * lot),
  };
}

/* ─────────────────────────────────────────────────────────────────
   SYNTHETIC OHLCV FOR PREVIEW
   Generates 25 days of realistic OHLCV so:
   - PDH / PDL come from yesterday (index n-1), within 0.5-2% of CMP
   - 5/10/20-day swing ranges are tight and realistic
   - Fibonacci levels land close to current price
   
   Key: daily range = ATR_day ≈ σ/√252 × CMP × 1.3
        PDH = last high ≈ CMP + 0.4-0.9 × ATR_day
        PDL = last low  ≈ CMP - 0.4-0.9 × ATR_day
   This matches what traders see on their charts every day.
───────────────────────────────────────────────────────────────── */
function synthOHLCV(cmp, sigma, mu, rng, nDays = 25) {
  const dt = 1 / 252;
  // ATR_day = typical daily range (≈1-3% of CMP for NSE stocks)
  const atr_day = cmp * sigma * Math.sqrt(dt) * 1.3;

  const cs = [cmp],
    hs = [cmp + atr_day * (0.4 + rng() * 0.5)],
    ls = [cmp - atr_day * (0.4 + rng() * 0.5)];
  let last = cmp;

  for (let i = 1; i < nDays; i++) {
    const u1 = Math.max(1e-10, rng()),
      u2 = rng();
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    const r = (mu - 0.5 * sigma * sigma) * dt + sigma * Math.sqrt(dt) * z;
    last *= Math.exp(r);
    // Daily H/L from ATR_day ± small random variation
    const dv = atr_day * (0.4 + rng() * 0.5); // intraday swing (realistic)
    const open_ = last * (1 + (rng() - 0.5) * 0.003);
    const h = Math.max(last, open_) + dv * (0.3 + rng() * 0.4);
    const l = Math.min(last, open_) - dv * (0.3 + rng() * 0.4);
    cs.push(+last.toFixed(2));
    hs.push(+h.toFixed(2));
    ls.push(+l.toFixed(2));
  }
  return { cs, hs, ls };
}

/* Block Bootstrap Simulation — no GBM */
function bootstrapSim(
  cmp,
  closes,
  mu,
  sigma,
  garch_vol,
  regime,
  nPaths = 500,
  days = 126
) {
  const bull_p = regime.bull_prob ?? 0.5;
  const bmu = (regime.bull_mu ?? mu * 1.3) / 252,
    bmu_ = (regime.bear_mu ?? -mu * 0.4) / 252;
  const rets = [];
  for (let i = 1; i < closes.length; i++)
    rets.push(Math.log(closes[i] / closes[i - 1]));
  if (rets.length < 5) {
    const p50 = Math.round(cmp * Math.exp((mu - 0.5 * sigma * sigma) * 0.5));
    return {
      prob_up: mu > 0 ? 0.6 : 0.4,
      prob_up_10: 0.3,
      prob_down_10: 0.25,
      final_p05: Math.round(p50 * 0.82),
      final_p25: Math.round(p50 * 0.92),
      final_p50: p50,
      final_p75: Math.round(p50 * 1.08),
      final_p95: Math.round(p50 * 1.18),
      bull_scenario_p50: Math.round(p50 * 1.1),
      bear_scenario_p50: Math.round(p50 * 0.9),
      max_drawdown_median: -0.1,
      max_drawdown_p95: -0.22,
      method: "fallback",
    };
  }
  const hv = Math.sqrt(rets.reduce((s, r) => s + r * r, 0) / rets.length);
  const cv = garch_vol / Math.sqrt(252);
  const vs = Math.min(2.5, Math.max(0.5, cv / (hv + 1e-9)));
  const hm = rets.reduce((a, b) => a + b, 0) / rets.length;
  const BL = 5,
    finals = [];
  let rs = 0xdeadbeef;
  const nr = () => {
    rs ^= rs << 13;
    rs ^= rs >> 17;
    rs ^= rs << 5;
    return (rs >>> 0) / 0xffffffff;
  };
  for (let p = 0; p < nPaths; p++) {
    const isBull = nr() < bull_p;
    let logS = Math.log(cmp);
    for (let d = 0; d < days; d++) {
      const bs = Math.floor(nr() * Math.max(1, rets.length - BL));
      const r = rets[bs + (d % BL)] ?? rets[bs];
      logS += (r - hm) * vs + (isBull ? bmu : bmu_);
    }
    finals.push(Math.exp(logS));
  }
  finals.sort((a, b) => a - b);
  const q = (f) =>
    finals[
      Math.min(finals.length - 1, Math.max(0, Math.floor(f * finals.length)))
    ];
  return {
    prob_up: +(finals.filter((f) => f > cmp).length / finals.length).toFixed(4),
    prob_up_10: +(
      finals.filter((f) => f > cmp * 1.1).length / finals.length
    ).toFixed(4),
    prob_down_10: +(
      finals.filter((f) => f < cmp * 0.9).length / finals.length
    ).toFixed(4),
    final_p05: Math.round(q(0.05)),
    final_p25: Math.round(q(0.25)),
    final_p50: Math.round(q(0.5)),
    final_p75: Math.round(q(0.75)),
    final_p95: Math.round(q(0.95)),
    final_mean: Math.round(finals.reduce((a, b) => a + b, 0) / finals.length),
    bull_scenario_p50: Math.round(q(0.7)),
    bear_scenario_p50: Math.round(q(0.25)),
    max_drawdown_median: -0.08 - (1 - (regime.bull_prob ?? 0.5)) * 0.1,
    max_drawdown_p95: -0.15 - (1 - (regime.bull_prob ?? 0.5)) * 0.18,
    method: "block_bootstrap_js",
  };
}

/* ─────────────── PREVIEW STOCKS ────────────────────────────── */
const PREVIEW = [
  { sym: "YESBANK", name: "Yes Bank", sec: "Banking", cmp: 25, lot: 4000 },
  { sym: "PNB", name: "Punjab Natl Bank", sec: "Banking", cmp: 100, lot: 2500 },
  {
    sym: "BANKBARODA",
    name: "Bank of Baroda",
    sec: "Banking",
    cmp: 245,
    lot: 1500,
  },
  {
    sym: "IDFCFIRSTB",
    name: "IDFC First Bank",
    sec: "Banking",
    cmp: 65,
    lot: 2500,
  },
  {
    sym: "BANDHANBNK",
    name: "Bandhan Bank",
    sec: "Banking",
    cmp: 175,
    lot: 1250,
  },
  {
    sym: "FEDERALBNK",
    name: "Federal Bank",
    sec: "Banking",
    cmp: 195,
    lot: 2500,
  },
  { sym: "WIPRO", name: "Wipro Ltd", sec: "IT", cmp: 475, lot: 1000 },
  {
    sym: "IRFC",
    name: "Indian Railway Finance",
    sec: "Finance",
    cmp: 175,
    lot: 2000,
  },
  {
    sym: "PFC",
    name: "Power Finance Corp",
    sec: "Finance",
    cmp: 475,
    lot: 600,
  },
  { sym: "RECLTD", name: "REC Limited", sec: "Finance", cmp: 478, lot: 600 },
  {
    sym: "MANAPPURAM",
    name: "Manappuram Finance",
    sec: "Finance",
    cmp: 175,
    lot: 2000,
  },
  { sym: "NHPC", name: "NHPC Ltd", sec: "Power", cmp: 88, lot: 4000 },
  { sym: "SJVN", name: "SJVN Ltd", sec: "Power", cmp: 118, lot: 3000 },
  { sym: "NTPC", name: "NTPC Ltd", sec: "Power", cmp: 355, lot: 1000 },
  { sym: "TATAPOWER", name: "Tata Power", sec: "Power", cmp: 395, lot: 1000 },
  { sym: "SUZLON", name: "Suzlon Energy", sec: "Power", cmp: 65, lot: 7000 },
  {
    sym: "NBCC",
    name: "NBCC India",
    sec: "Infrastructure",
    cmp: 95,
    lot: 4000,
  },
  {
    sym: "RVNL",
    name: "Rail Vikas Nigam",
    sec: "Infrastructure",
    cmp: 475,
    lot: 750,
  },
  {
    sym: "BEL",
    name: "Bharat Electronics",
    sec: "Defence",
    cmp: 285,
    lot: 1250,
  },
  { sym: "BHEL", name: "BHEL", sec: "Capital Goods", cmp: 235, lot: 1500 },
  { sym: "ASHOKLEY", name: "Ashok Leyland", sec: "Auto", cmp: 218, lot: 2000 },
  {
    sym: "MOTHERSON",
    name: "Samvardhana Motherson",
    sec: "Auto",
    cmp: 168,
    lot: 3000,
  },
  {
    sym: "GRANULES",
    name: "Granules India",
    sec: "Pharma",
    cmp: 495,
    lot: 600,
  },
  { sym: "LAURUSLABS", name: "Laurus Labs", sec: "Pharma", cmp: 478, lot: 750 },
  { sym: "BIOCON", name: "Biocon Ltd", sec: "Pharma", cmp: 285, lot: 1500 },
  { sym: "SAIL", name: "Steel Authority", sec: "Metals", cmp: 118, lot: 3000 },
  { sym: "NMDC", name: "NMDC Ltd", sec: "Metals", cmp: 218, lot: 2000 },
  {
    sym: "HINDCOPPER",
    name: "Hindustan Copper",
    sec: "Metals",
    cmp: 308,
    lot: 1500,
  },
  { sym: "TRIDENT", name: "Trident Ltd", sec: "Textiles", cmp: 38, lot: 8000 },
  { sym: "ITC", name: "ITC Ltd", sec: "FMCG", cmp: 455, lot: 1200 },
  { sym: "DABUR", name: "Dabur India", sec: "FMCG", cmp: 495, lot: 1000 },
  {
    sym: "AARTIIND",
    name: "Aarti Industries",
    sec: "Chemicals",
    cmp: 428,
    lot: 750,
  },
  { sym: "UPL", name: "UPL Ltd", sec: "Agrochemicals", cmp: 395, lot: 1000 },
  { sym: "IDEA", name: "Vodafone Idea", sec: "Telecom", cmp: 12, lot: 30000 },
  { sym: "ZEEL", name: "Zee Entertainment", sec: "Media", cmp: 128, lot: 3000 },
  {
    sym: "BALRAMCHIN",
    name: "Balrampur Chini",
    sec: "Sugar",
    cmp: 395,
    lot: 1000,
  },
  { sym: "IREDA", name: "IREDA", sec: "Finance", cmp: 185, lot: 1500 },
  {
    sym: "LEMONTREE",
    name: "Lemon Tree Hotels",
    sec: "Hotels",
    cmp: 115,
    lot: 3000,
  },
  { sym: "GATI", name: "Gati Ltd", sec: "Logistics", cmp: 115, lot: 3000 },
];

/**
 * buildPreviewModel — runs the exact same algorithm chain as Python:
 * 1. Synthetic OHLCV (25 days, realistic daily ATR)
 * 2. computeCPR   → PDH/PDL/TC/BC/Fib within 1-3% of CMP
 * 3. computeST    → Supertrend direction/level
 * 4. ATR14        → from actual true-range array
 * 5. bootstrapSim → block bootstrap (no GBM)
 * 6. generateSignals → CPR zones, same as Python
 */
function buildPreviewModel(stocks) {
  const seen = new Set();
  return stocks
    .filter((s) => {
      if (seen.has(s.sym)) return false;
      seen.add(s.sym);
      return true;
    })
    .map((s, i) => {
      const rng = mkRng(0xcafe0000 + i);
      const mu = 0.04 + rng() * 0.26,
        sigma = 0.14 + rng() * 0.28;
      const garch_vol = sigma * (0.85 + rng() * 0.4);
      const bull_p = Math.min(0.92, Math.max(0.08, 0.45 + mu / 2));
      const sw_prob = 0.1 + rng() * 0.55;
      const reg_cur =
        bull_p >= 0.6 ? "Bull" : bull_p <= 0.4 ? "Bear" : "Transitioning";
      const cmp = s.cmp;

      const regime = {
        current: reg_cur,
        bull_prob: +bull_p.toFixed(4),
        bear_prob: +(1 - bull_p).toFixed(4),
        switch_prob: +sw_prob.toFixed(4),
        bull_mu: +(mu * 1.3).toFixed(4),
        bear_mu: +(-mu * 0.4).toFixed(4),
        last_3m_ret: +(rng() * 0.3 - 0.05).toFixed(4),
      };

      // 25 days realistic OHLCV — PDH/PDL within 1-3% of CMP
      const { cs, hs, ls } = synthOHLCV(cmp, sigma, mu, rng, 25);

      // CPR from realistic OHLCV (PDH ≈ CMP±1%, TC/BC very tight)
      const cpr = computeCPR(hs, ls, cs);

      // ATR14
      const trA = [];
      for (let j = 1; j < cs.length; j++)
        trA.push(
          Math.max(
            hs[j] - ls[j],
            Math.abs(hs[j] - cs[j - 1]),
            Math.abs(ls[j] - cs[j - 1])
          )
        );
      const atr14 =
        trA.slice(-14).reduce((a, b) => a + b, 0) / Math.min(14, trA.length);

      // Supertrend (need at least period+5 bars; if not enough, it falls back)
      const st = computeST(hs, ls, cs);

      // Technicals
      const rsi = 28 + rng() * 44,
        macd_h = rng() > 0.5 ? rng() * 5 : -rng() * 5;
      const obv_tr = rng() > 0.5 ? 1 : -1,
        golden = rng() > 0.5,
        vol_r = 0.7 + rng() * 0.9;
      const tech = {
        rsi14: +rsi.toFixed(2),
        atr14: +atr14.toFixed(2),
        macd_hist: +macd_h.toFixed(4),
        obv_trend: obv_tr,
        golden_cross: golden,
        vol_ratio: +vol_r.toFixed(3),
        stoch_k: +(20 + rng() * 60).toFixed(2),
        bb_pct: +rng().toFixed(4),
        from_52h: -(rng() * 30).toFixed(2),
        from_52l: +(rng() * 40).toFixed(2),
      };

      // Block Bootstrap simulation
      const sim = bootstrapSim(cmp, cs, mu, sigma, garch_vol, regime);

      // Signals — CPR-anchored (levels will be within 1-3% of CMP)
      const mcmc_ = { mu, sigma, acceptance: 0.23 + rng() * 0.2 };
      const garch_ = {
        cond_vol_annual: garch_vol,
        persistence: 0.85 + rng() * 0.1,
        long_run_vol: sigma,
      };
      const sigs = generateSignals(
        cmp,
        mcmc_,
        garch_,
        regime,
        sim,
        tech,
        cpr,
        st,
        s.lot
      );

      // Monthly & chart projections
      const monthly = [1, 2, 3, 4, 5, 6].map((m) => {
        const t = m / 12,
          d = (mu - 0.5 * sigma * sigma) * t,
          bnd = sigma * Math.sqrt(t);
        const base = Math.round(cmp * Math.exp(d));
        return {
          month: `Month ${m}`,
          low: Math.round(cmp * Math.exp(d - 1.28 * bnd)),
          base,
          high: Math.round(cmp * Math.exp(d + 1.28 * bnd)),
          chg: +(((base - cmp) / cmp) * 100).toFixed(1),
        };
      });
      const chart = cs
        .filter((_, j) => j % 1 === 0)
        .slice(0, 22)
        .map((c, j) => {
          const bnd = sigma * Math.sqrt((j + 1) / 252);
          return {
            w: j === 0 ? "Now" : `D${j * 2}`,
            low: Math.round(c * (1 - 1.28 * bnd)),
            base: Math.round(c),
            high: Math.round(c * (1 + 1.28 * bnd)),
          };
        });

      return {
        symbol: s.sym,
        name: s.name,
        sector: s.sec,
        cmp,
        lot: s.lot,
        mcmc: mcmc_,
        garch: garch_,
        regime,
        cpr,
        supertrend: st,
        simulation: sim,
        signals: sigs,
        technicals: tech,
        monthly,
        chart,
        target: sim.final_p50 ?? monthly[5].base,
        bear: sim.final_p05 ?? monthly[5].low,
        bull: sim.final_p95 ?? monthly[5].high,
        target6m: +(
          (((sim.final_p50 ?? monthly[5].base) - cmp) / cmp) *
          100
        ).toFixed(1),
        isReal: false,
      };
    });
}

const BASE_MODEL = buildPreviewModel(PREVIEW);

/* ─────────────────────────────────────────────────────────────────
   JSON ENRICHER — for real mcmc_results.json
   
   Rule: Python already computed all trade levels correctly (PDH/PDL
   from real market data). Use signals AS-IS. Only recompute if the
   JSON is missing entry_long (very old format).
───────────────────────────────────────────────────────────────── */
function enrichFromJSON(stocks) {
  return stocks.map((s) => {
    const sim = s.simulation || {},
      mcmc = s.mcmc || {};
    const sigs = s.signals || {},
      tech = s.technicals || {};
    const reg = s.regime || {},
      ga = s.garch || {};
    const cpr = s.cpr || {}; // ← Python outputs 's.cpr' (not camarilla)
    const st = s.supertrend || {};
    const cmp = s.cmp || 100,
      lot = s.lot_size || s.lot || 1000;
    const tl = sim.timeline || [];

    // Only recompute signals if Python entry_long is absent (old JSON)
    let finalSigs = sigs;
    if (!sigs.entry_long && cpr.pdh) {
      finalSigs = generateSignals(
        cmp,
        mcmc,
        ga,
        reg,
        sim,
        { ...tech, atr14: tech.atr14 ?? cmp * 0.015 },
        cpr,
        st,
        lot
      );
    }

    // Monthly from timeline
    const monthly = [1, 2, 3, 4, 5, 6].map((m) => {
      const d = tl[Math.min(m * 21, tl.length - 1)] || {};
      const base = Math.round(d.p50 || cmp);
      return {
        month: `Month ${m}`,
        low: Math.round(d.p5 || d.p10 || cmp * 0.88),
        base,
        high: Math.round(d.p95 || d.p90 || cmp * 1.12),
        chg: +(((base - cmp) / cmp) * 100).toFixed(1),
      };
    });
    const rc = tl.filter((_, i) => i % 6 === 0).slice(0, 22);
    const chart =
      rc.length > 0
        ? rc.map((d, j) => ({
            w: j === 0 ? "Now" : `W${j * 2}`,
            low: Math.round(d.p5 || d.p10 || cmp),
            base: Math.round(d.p50 || cmp),
            high: Math.round(d.p95 || d.p90 || cmp),
          }))
        : [{ w: "Now", low: cmp, base: cmp, high: cmp }];

    return {
      symbol: s.symbol || s.s,
      name: s.name || s.n || s.symbol,
      sector: s.sector || s.sec || "NSE Listed",
      cmp,
      lot,
      mcmc,
      garch: ga,
      regime: reg,
      cpr,
      supertrend: st,
      simulation: sim,
      signals: finalSigs,
      technicals: tech,
      monthly,
      chart,
      target: Math.round(sim.final_p50 || monthly[5]?.base || cmp),
      bear: Math.round(sim.final_p05 || monthly[5]?.low || cmp * 0.85),
      bull: Math.round(sim.final_p95 || monthly[5]?.high || cmp * 1.15),
      target6m: +((((sim.final_p50 || cmp) - cmp) / cmp) * 100).toFixed(1),
      isReal: true,
    };
  });
}

/* ══════════════════════════════════════════════════════════════════
   UI COMPONENTS
══════════════════════════════════════════════════════════════════ */
function ScoreRing({ score, col, size = 52 }) {
  const r = size / 2 - 4,
    circ = 2 * Math.PI * r,
    dash = (score / 100) * circ;
  return (
    <div
      style={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#1e2d3d"
          strokeWidth="3.5"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={col}
          strokeWidth="3.5"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
        />
      </svg>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...mono({
            fontSize: size < 48 ? 9 : 12,
            fontWeight: 700,
            color: col,
          }),
        }}
      >
        {score}
      </div>
    </div>
  );
}
function RegimeBadge({ regime }) {
  if (!regime?.current) return null;
  const M = {
    Bull: { bg: "#052e16", br: "#166534", tx: GR, ic: "▲" },
    Bear: { bg: "#450a0a", br: "#991b1b", tx: RD, ic: "▼" },
    Transitioning: { bg: "#422006", br: "#92400e", tx: AM, ic: "◆" },
    Unknown: { bg: "#0f172a", br: "#334155", tx: "#94a3b8", ic: "?" },
  };
  const s = M[regime.current] || M.Unknown;
  return (
    <span
      style={{
        background: s.bg,
        border: `1px solid ${s.br}`,
        color: s.tx,
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {s.ic} {regime.current}
      {regime.switch_prob != null && (
        <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 2 }}>
          ({(regime.switch_prob * 100).toFixed(0)}% sw)
        </span>
      )}
    </span>
  );
}
function ChartTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#0d1117",
        border: "1px solid #2a3441",
        borderRadius: 8,
        padding: "10px 14px",
        fontSize: 11,
      }}
    >
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, ...mono({ marginBottom: 2 }) }}>
          {p.name}: ₹{fi(p.value)}
        </div>
      ))}
    </div>
  );
}
function GRow({ label, value, desc }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 0",
        borderBottom: "1px solid #1a2332",
      }}
    >
      <div>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>{label}</div>
        <div style={{ fontSize: 10, color: "#475569", marginTop: 1 }}>
          {desc}
        </div>
      </div>
      <div style={mono({ fontSize: 14, fontWeight: 700, color: AM })}>
        {value}
      </div>
    </div>
  );
}
function ScoreRadar({ bd }) {
  if (!bd) return null;
  const L = {
    prob_up: "Prob↑",
    mu_drift: "Drift",
    low_vol: "LowVol",
    rsi: "RSI",
    macd: "MACD",
    obv: "OBV",
    regime: "Regime",
    golden: "GoldenX",
    supertrend: "ST",
    stoch: "Stoch",
  };
  const data = Object.entries(L).map(([k, label]) => ({
    label,
    value: Math.max(0, Math.round(bd[k] || 0)),
    fullMark: 30,
  }));
  return (
    <ResponsiveContainer width="100%" height={190}>
      <RadarChart
        data={data}
        margin={{ top: 10, right: 24, bottom: 10, left: 24 }}
      >
        <PolarGrid stroke="#1e2d3d" />
        <PolarAngleAxis
          dataKey="label"
          tick={{ fill: "#64748b", fontSize: 9 }}
        />
        <Radar dataKey="value" stroke={AM} fill={AM} fillOpacity={0.15} />
      </RadarChart>
    </ResponsiveContainer>
  );
}
function StockRow({ stock, selected, onClick }) {
  const sc_ = SCOL[stock.sector] || "#475569";
  const sig = stock.signals || {},
    vc = sig.verdict_color || AM;
  const t6 = stock.target6m ?? 0,
    score = sig.score || 50;
  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 62px 54px 44px",
        alignItems: "center",
        padding: "9px 10px",
        borderRadius: 8,
        marginBottom: 2,
        cursor: "pointer",
        background: selected ? "#0d1f33" : "transparent",
        border: `1px solid ${selected ? "#1e3a5f" : "transparent"}`,
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = "#0a1628";
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = "transparent";
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: sc_,
            flexShrink: 0,
          }}
        />
        <div style={{ overflow: "hidden" }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 12,
              color: "#e2e8f0",
              whiteSpace: "nowrap",
            }}
          >
            {stock.symbol}
          </div>
          <div
            style={{
              fontSize: 9,
              color: "#475569",
              marginTop: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            ₹{fi(stock.cmp)} · {stock.sector}
          </div>
        </div>
      </div>
      <div
        style={mono({
          fontSize: 12,
          fontWeight: 700,
          color: "#94a3b8",
          textAlign: "right",
        })}
      >
        ₹{fi(stock.cmp)}
      </div>
      <div
        style={mono({
          fontSize: 11,
          fontWeight: 700,
          textAlign: "center",
          color: t6 >= 0 ? GR : RD,
        })}
      >
        {t6 >= 0 ? "+" : ""}
        {t6}%
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <ScoreRing score={score} col={vc} size={38} />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   DETAIL PANEL
══════════════════════════════════════════════════════════════════ */
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "regime", label: "Regime" },
  { id: "targets", label: "Targets" },
  { id: "trade", label: "Trade" },
  { id: "options", label: "Options" },
];

function DetailPanel({ stock }) {
  const [tab, setTab] = useState("overview");
  if (!stock) return null;
  const sig = stock.signals || {},
    reg = stock.regime || {};
  const sim = stock.simulation || {},
    mc = stock.mcmc || {};
  const ga = stock.garch || {},
    tech = stock.technicals || {};
  const sc_ = SCOL[stock.sector] || "#475569";
  const vc = sig.verdict_color || AM,
    score = sig.score || 50;
  const t6 = stock.target6m ?? 0,
    probUp = sim.prob_up ?? 0.5;
  const mu = mc.mu ?? 0,
    sigma = mc.sigma ?? 0.25,
    gv = ga.cond_vol_annual ?? sigma;
  const atr14 = tech.atr14 ?? stock.cmp * 0.015;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        height: "100%",
        overflowY: "auto",
        padding: 18,
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#0d1117",
          borderRadius: 14,
          padding: "18px 22px",
          border: "1px solid #1e2d3d",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 12,
              background: `${sc_}18`,
              border: `1px solid ${sc_}30`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
            }}
          >
            {SEMO[stock.sector] || "🏭"}
          </div>
          <div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#f1f5f9",
                letterSpacing: -0.5,
              }}
            >
              {stock.symbol}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {stock.name}
            </div>
            <div
              style={{
                display: "flex",
                gap: 6,
                marginTop: 6,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  background: `${sc_}18`,
                  color: sc_,
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {stock.sector}
              </span>
              <span
                style={{
                  background: "#1a2332",
                  color: "#64748b",
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 10,
                }}
              >
                Lot: {fi(stock.lot)}
              </span>
              <RegimeBadge regime={reg} />
              {!stock.isReal && (
                <span
                  style={{
                    background: "#422006",
                    color: AM,
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontSize: 9,
                  }}
                >
                  Preview
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={mono({
              fontSize: 28,
              fontWeight: 700,
              color: "#f1f5f9",
              letterSpacing: -1,
            })}
          >
            ₹{fi(stock.cmp)}
          </div>
          <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>
            NSE Price
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
              alignItems: "center",
              marginTop: 8,
            }}
          >
            <ScoreRing score={score} col={vc} size={52} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: vc }}>
                {sig.verdict}
              </div>
              <div style={{ fontSize: 10, color: "#475569" }}>MCMC Score</div>
            </div>
          </div>
        </div>
      </div>

      {/* Philosophy */}
      <div
        style={{
          background: "#0a0f18",
          border: "1px solid #1a2332",
          borderLeft: `3px solid ${AM}`,
          borderRadius: 10,
          padding: "10px 16px",
          display: "flex",
          gap: 10,
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: 16 }}>🗺️</span>
        <div>
          <div
            style={{
              fontFamily: "'Playfair Display',serif",
              fontStyle: "italic",
              fontSize: 11,
              color: "#64748b",
              lineHeight: 1.6,
            }}
          >
            "History is the study of changes — ironically used as a map for the
            future"
          </div>
          <div style={{ fontSize: 10, color: "#334155", marginTop: 2 }}>
            {stock.isReal ? "Real 2Y NSE data" : "Estimated"} → GARCH + MCMC →
            Bootstrap → 6M median:{" "}
            <span style={{ color: t6 >= 0 ? GR : RD, fontWeight: 700 }}>
              ₹{fi(stock.target)} ({t6 >= 0 ? "+" : ""}
              {t6}%)
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: 3,
          background: "#0d1117",
          borderRadius: 10,
          padding: 4,
          border: "1px solid #1e2d3d",
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              flex: 1,
              padding: "8px 4px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              border: "none",
              background: tab === t.id ? "#1e3a5f" : "transparent",
              color: tab === t.id ? CY : "#475569",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div
            style={{
              background: `${vc}08`,
              border: `1px solid ${vc}25`,
              borderRadius: 12,
              padding: "14px 16px",
            }}
          >
            <div
              style={{
                fontWeight: 800,
                fontSize: 13,
                color: "#e2e8f0",
                marginBottom: 6,
              }}
            >
              🤖 Model findings
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.75 }}>
              <span style={{ color: vc, fontWeight: 700 }}>
                {(probUp * 100).toFixed(0)}% chance
              </span>{" "}
              of being higher in 6 months. Most likely:{" "}
              <span style={{ color: "#f1f5f9", fontWeight: 700 }}>
                ₹{fi(stock.target)}
              </span>{" "}
              (
              <span style={{ color: t6 >= 0 ? GR : RD, fontWeight: 700 }}>
                {t6 >= 0 ? "+" : ""}
                {t6}%
              </span>
              ). Bull:{" "}
              <span style={{ color: BL, fontWeight: 700 }}>
                ₹{fi(stock.bull)}
              </span>{" "}
              · Bear:{" "}
              <span style={{ color: RD, fontWeight: 700 }}>
                ₹{fi(stock.bear)}
              </span>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 8,
            }}
          >
            {[
              {
                e: "🎯",
                l: "6M Target",
                v: `₹${fi(stock.target)}`,
                s: `${t6 >= 0 ? "+" : ""}${t6}%`,
                c: t6 >= 0 ? GR : RD,
              },
              {
                e: "🚀",
                l: "Bull Case",
                v: `₹${fi(stock.bull)}`,
                s: "P95 paths",
                c: BL,
              },
              {
                e: "🛡️",
                l: "Bear Case",
                v: `₹${fi(stock.bear)}`,
                s: "P5 paths",
                c: RD,
              },
              {
                e: "🎲",
                l: "Prob Up",
                v: `${(probUp * 100).toFixed(0)}%`,
                s: "bootstrap",
                c: probUp >= 0.55 ? GR : probUp >= 0.45 ? AM : RD,
              },
            ].map(({ e, l, v, s, c }) => (
              <div
                key={l}
                style={{
                  background: "#0d1117",
                  borderRadius: 10,
                  padding: "12px 14px",
                  border: "1px solid #1e2d3d",
                  borderTop: `3px solid ${c}`,
                }}
              >
                <div
                  style={{ fontSize: 10, color: "#475569", marginBottom: 4 }}
                >
                  {e} {l}
                </div>
                <div style={mono({ fontSize: 16, fontWeight: 700, color: c })}>
                  {v}
                </div>
                <div style={{ fontSize: 9, color: "#334155", marginTop: 3 }}>
                  {s}
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              background: "#0d1117",
              borderRadius: 12,
              padding: "14px 16px",
              border: "1px solid #1e2d3d",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#e2e8f0",
                marginBottom: 2,
              }}
            >
              📉 6M Price Band
            </div>
            <div style={{ fontSize: 10, color: "#475569", marginBottom: 10 }}>
              Bootstrap · GARCH · {reg.current} regime ·{" "}
              {stock.isReal ? "Real data" : "Preview"}
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={stock.chart || []}>
                <CartesianGrid strokeDasharray="3 8" stroke="#1a2332" />
                <XAxis
                  dataKey="w"
                  tick={{ fill: "#475569", fontSize: 9 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#475569", fontSize: 9 }}
                  axisLine={false}
                  tickLine={false}
                  width={46}
                  tickFormatter={(v) =>
                    v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`
                  }
                  domain={["auto", "auto"]}
                />
                <Tooltip content={<ChartTip />} />
                <Area
                  type="monotone"
                  dataKey="high"
                  fill="#052e1640"
                  stroke="#166534"
                  strokeWidth={1}
                  name="Optimistic"
                />
                <Area
                  type="monotone"
                  dataKey="base"
                  fill="#052e1660"
                  stroke={GR}
                  strokeWidth={2}
                  name="Most Likely"
                />
                <Area
                  type="monotone"
                  dataKey="low"
                  fill="#0d111720"
                  stroke="#2a3441"
                  strokeWidth={1}
                  name="Pessimistic"
                />
                <ReferenceLine
                  y={stock.cmp}
                  stroke={AM}
                  strokeDasharray="4 3"
                  strokeWidth={2}
                  label={{
                    value: "Today",
                    fill: AM,
                    fontSize: 9,
                    fontWeight: 700,
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div
            style={{
              background: "#0d1117",
              borderRadius: 12,
              padding: "14px 16px",
              border: "1px solid #1e2d3d",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#e2e8f0",
                marginBottom: 10,
              }}
            >
              🔍 Technicals
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              {[
                {
                  l: "Annual Drift (μ)",
                  v: `${mu >= 0 ? "+" : ""}${(mu * 100).toFixed(1)}%/yr`,
                  n:
                    mu > 0.15
                      ? "Strong upward"
                      : mu > 0
                      ? "Mild positive"
                      : "Historical downtrend",
                  good: mu > 0.05,
                  e: "📈",
                },
                {
                  l: "MCMC σ",
                  v: `${(sigma * 100).toFixed(0)}%/yr`,
                  n:
                    sigma > 0.35
                      ? "High risk+reward"
                      : sigma > 0.22
                      ? "Moderate"
                      : "Stable",
                  good: sigma < 0.3,
                  e: "🌊",
                },
                {
                  l: "GARCH Vol",
                  v: `${(gv * 100).toFixed(1)}%/yr`,
                  n:
                    gv > sigma * 1.2
                      ? "Vol spike — caution"
                      : "Normal clustering",
                  good: gv <= sigma * 1.15,
                  e: "⚙️",
                },
                {
                  l: "RSI 14",
                  v: `${(tech.rsi14 || 50).toFixed(0)}`,
                  n:
                    (tech.rsi14 || 50) > 70
                      ? "Overbought"
                      : (tech.rsi14 || 50) < 30
                      ? "Oversold"
                      : "Neutral",
                  good: (tech.rsi14 || 50) >= 35 && (tech.rsi14 || 50) <= 65,
                  e: "⚡",
                },
                {
                  l: "Golden Cross",
                  v: tech.golden_cross ? "Yes ✓" : "No ✗",
                  n: tech.golden_cross ? "SMA50>SMA200" : "SMA50<SMA200",
                  good: !!tech.golden_cross,
                  e: "✨",
                },
                {
                  l: "ATR14 (daily range)",
                  v: `₹${fi(atr14, 1)}`,
                  n: `${((atr14 / stock.cmp) * 100).toFixed(2)}% of CMP`,
                  good: true,
                  e: "📏",
                },
              ].map(({ l, v, n, good, e }) => (
                <div
                  key={l}
                  style={{
                    background: good ? "#052e1620" : "#450a0a20",
                    border: `1px solid ${good ? "#166534" : "#7f1d1d"}`,
                    borderRadius: 9,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{ fontSize: 10, color: "#64748b", marginBottom: 3 }}
                  >
                    {e} {l}
                  </div>
                  <div
                    style={mono({
                      fontSize: 15,
                      fontWeight: 700,
                      color: good ? GR : RD,
                    })}
                  >
                    {v}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: good ? "#166534" : "#7f1d1d",
                      marginTop: 3,
                    }}
                  >
                    {n}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              background: "#0d1117",
              borderRadius: 12,
              padding: "14px 16px",
              border: "1px solid #1e2d3d",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#e2e8f0",
                marginBottom: 6,
              }}
            >
              🕸️ Score Radar
            </div>
            <ScoreRadar bd={sig.score_breakdown} />
          </div>
        </div>
      )}

      {/* ── REGIME ── */}
      {tab === "regime" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div
            style={{
              background:
                reg.current === "Bull"
                  ? "#052e16"
                  : reg.current === "Bear"
                  ? "#450a0a"
                  : "#422006",
              border: `1px solid ${
                reg.current === "Bull" ? GR : reg.current === "Bear" ? RD : AM
              }`,
              borderRadius: 14,
              padding: "20px 22px",
            }}
          >
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
              MARKOV REGIME DETECTION
            </div>
            <div
              style={{
                fontSize: 32,
                fontWeight: 800,
                color:
                  reg.current === "Bull"
                    ? GR
                    : reg.current === "Bear"
                    ? RD
                    : AM,
              }}
            >
              {reg.current === "Bull"
                ? "▲ BULL MARKET"
                : reg.current === "Bear"
                ? "▼ BEAR MARKET"
                : "◆ TRANSITIONING"}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#94a3b8",
                marginTop: 8,
                lineHeight: 1.7,
              }}
            >
              Switch risk:{" "}
              <b style={{ color: AM }}>
                {((reg.switch_prob || 0) * 100).toFixed(0)}%
              </b>
            </div>
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
          >
            {[
              {
                l: "Bull Prob",
                v: `${((reg.bull_prob || 0.5) * 100).toFixed(1)}%`,
                c: GR,
              },
              {
                l: "Bear Prob",
                v: `${((reg.bear_prob || 0.5) * 100).toFixed(1)}%`,
                c: RD,
              },
              {
                l: "Switch Risk",
                v: `${((reg.switch_prob || 0) * 100).toFixed(1)}%`,
                c: AM,
              },
              {
                l: "Last 3M",
                v: `${((reg.last_3m_ret || 0) * 100).toFixed(1)}%`,
                c: (reg.last_3m_ret || 0) >= 0 ? GR : RD,
              },
              {
                l: "Bull Drift",
                v: `${((reg.bull_mu || 0) * 100).toFixed(1)}%/yr`,
                c: GR,
              },
              {
                l: "Bear Drift",
                v: `${((reg.bear_mu || 0) * 100).toFixed(1)}%/yr`,
                c: RD,
              },
            ].map(({ l, v, c }) => (
              <div
                key={l}
                style={{
                  background: "#0d1117",
                  borderRadius: 10,
                  padding: "14px 16px",
                  border: "1px solid #1e2d3d",
                }}
              >
                <div
                  style={{ fontSize: 10, color: "#475569", marginBottom: 4 }}
                >
                  {l}
                </div>
                <div style={mono({ fontSize: 20, fontWeight: 700, color: c })}>
                  {v}
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              background: "#0d1117",
              borderRadius: 12,
              padding: "16px 18px",
              border: "1px solid #1e2d3d",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#e2e8f0",
                marginBottom: 3,
              }}
            >
              ⚙️ GARCH(1,1)
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3,1fr)",
                gap: 8,
              }}
            >
              {[
                {
                  l: "GARCH Vol",
                  v: `${((ga.cond_vol_annual || sigma) * 100).toFixed(1)}%`,
                },
                {
                  l: "Long-Run Vol",
                  v: `${((ga.long_run_vol || sigma) * 100).toFixed(1)}%`,
                },
                {
                  l: "Persistence",
                  v: `${((ga.persistence || 0.9) * 100).toFixed(1)}%`,
                },
              ].map(({ l, v }) => (
                <div
                  key={l}
                  style={{
                    background: "#080c12",
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{ fontSize: 10, color: "#475569", marginBottom: 3 }}
                  >
                    {l}
                  </div>
                  <div
                    style={mono({ fontSize: 16, fontWeight: 700, color: CY })}
                  >
                    {v}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
          >
            {[
              {
                l: "▲ Bull 6M",
                v: `₹${fi(sig.bull_target_6m || stock.bull)}`,
                c: GR,
                bg: "#052e16",
                br: "#166534",
              },
              {
                l: "▼ Bear 6M",
                v: `₹${fi(sig.bear_target_6m || stock.bear)}`,
                c: RD,
                bg: "#450a0a",
                br: "#991b1b",
              },
            ].map(({ l, v, c, bg, br }) => (
              <div
                key={l}
                style={{
                  background: bg,
                  border: `1px solid ${br}`,
                  borderRadius: 12,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: c,
                    marginBottom: 6,
                  }}
                >
                  {l}
                </div>
                <div
                  style={mono({
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#f1f5f9",
                  })}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TARGETS ── */}
      {tab === "targets" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div
            style={{
              background: "#0d1117",
              borderRadius: 12,
              padding: "14px 16px",
              border: "1px solid #1e2d3d",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#e2e8f0",
                marginBottom: 10,
              }}
            >
              📅 Month-by-Month
            </div>
            {(stock.monthly || []).map((m, i) => (
              <div
                key={i}
                style={{
                  background: "#080c12",
                  borderRadius: 9,
                  padding: "10px 12px",
                  marginBottom: 7,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ fontWeight: 700, color: "#e2e8f0" }}>
                    {m.month}
                  </span>
                  <span
                    style={mono({
                      background: m.chg >= 0 ? "#052e16" : "#450a0a",
                      color: m.chg >= 0 ? GR : RD,
                      padding: "2px 8px",
                      borderRadius: 5,
                      fontSize: 11,
                      fontWeight: 700,
                    })}
                  >
                    {m.chg >= 0 ? "+" : ""}
                    {m.chg}%
                  </span>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div
                    style={mono({
                      fontSize: 17,
                      fontWeight: 700,
                      color: "#f1f5f9",
                      minWidth: 80,
                    })}
                  >
                    ₹{fi(m.base)}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      background: "#1a2332",
                      borderRadius: 4,
                      height: 6,
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(100, Math.abs(m.chg) * 3.5)}%`,
                        height: "100%",
                        borderRadius: 4,
                        background: m.chg >= 0 ? GR : RD,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 8,
            }}
          >
            {[
              {
                l: "😟 Bear",
                p: stock.bear,
                c: RD,
                bg: "#450a0a",
                br: "#991b1b",
              },
              {
                l: "😊 Base",
                p: stock.target,
                c: GR,
                bg: "#052e16",
                br: "#166534",
              },
              {
                l: "🚀 Bull",
                p: stock.bull,
                c: BL,
                bg: "#0c1a3a",
                br: "#1e3a8a",
              },
            ].map(({ l, p, c, bg, br }) => {
              const chg = (((p - stock.cmp) / stock.cmp) * 100).toFixed(1);
              return (
                <div
                  key={l}
                  style={{
                    background: bg,
                    border: `1px solid ${br}`,
                    borderRadius: 12,
                    padding: 16,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: c,
                      marginBottom: 6,
                    }}
                  >
                    {l}
                  </div>
                  <div
                    style={mono({
                      fontSize: 20,
                      fontWeight: 700,
                      color: "#f1f5f9",
                    })}
                  >
                    ₹{fi(p)}
                  </div>
                  <div
                    style={mono({
                      fontSize: 13,
                      color: c,
                      fontWeight: 700,
                      marginTop: 3,
                    })}
                  >
                    {chg >= 0 ? "+" : ""}
                    {chg}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TRADE ── */}
      {tab === "trade" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Supertrend banner */}
          {(() => {
            const up = sig.supertrend_dir?.includes("Bull") ?? true;
            return (
              <div
                style={{
                  background: up ? "#052e16" : "#450a0a",
                  border: `1px solid ${up ? GR : RD}`,
                  borderRadius: 12,
                  padding: "12px 16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>
                    SUPERTREND (10, 3)
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      color: up ? GR : RD,
                      marginTop: 2,
                    }}
                  >
                    {up ? "▲ BULLISH TREND" : "▼ BEARISH TREND"}
                  </div>
                  <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>
                    Support line:{" "}
                    <span style={mono({ color: "#94a3b8", fontWeight: 700 })}>
                      ₹{fi(sig.supertrend_level ?? stock.cmp)}
                    </span>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 10, color: "#475569" }}>
                    Zone / Strategy
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: AM,
                      marginTop: 2,
                    }}
                  >
                    {sig.trade_type || "—"}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* CPR Levels */}
          {(() => {
            const cpr_ = stock.cpr || {};
            const pdh = sig.pdh ?? cpr_.pdh ?? stock.cmp * 1.01;
            const pdl = sig.pdl ?? cpr_.pdl ?? stock.cmp * 0.99;
            const tc = sig.cpr_tc ?? cpr_.tc ?? stock.cmp * 1.003;
            const bc = sig.cpr_bc ?? cpr_.bc ?? stock.cmp * 0.997;
            const wp = sig.cpr_width ?? cpr_.width ?? stock.cmp * 0.005;
            const pt = sig.cpr_pivot ?? cpr_.pivot ?? stock.cmp;
            const ctype = sig.cpr_type ?? cpr_.cpr_type ?? "";
            const pct_ = (v) =>
              (((v - stock.cmp) / stock.cmp) * 100).toFixed(2);
            const near = (v) => Math.abs(v - stock.cmp) / stock.cmp < 0.008;
            return (
              <div
                style={{
                  background: "#0d1117",
                  borderRadius: 12,
                  padding: "14px 16px",
                  border: "1px solid #1e2d3d",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 3,
                  }}
                >
                  <div
                    style={{ fontWeight: 700, fontSize: 13, color: "#e2e8f0" }}
                  >
                    🎯 CPR · PDH/PDL Levels
                  </div>
                  <span
                    style={{
                      background: ctype.includes("Narrow")
                        ? "#052e16"
                        : ctype.includes("Wide")
                        ? "#450a0a"
                        : "#1a2332",
                      color: ctype.includes("Narrow")
                        ? GR
                        : ctype.includes("Wide")
                        ? RD
                        : AM,
                      padding: "2px 8px",
                      borderRadius: 5,
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {ctype || "Normal"}
                  </span>
                </div>
                <div
                  style={{ fontSize: 10, color: "#475569", marginBottom: 10 }}
                >
                  PDH/PDL from yesterday's session · CMP: ₹{fi(stock.cmp)}
                </div>
                {[
                  {
                    l: "PDH (Prev Day High)",
                    v: pdh,
                    c: RD,
                    desc: "Resistance — breakout above = bullish",
                  },
                  {
                    l: "CPR Top (TC)",
                    v: tc,
                    c: "#f97316",
                    desc: "Top of Central Pivot Range",
                  },
                  {
                    l: "CPR Pivot",
                    v: pt,
                    c: AM,
                    desc: "Central pivot — midpoint reference",
                  },
                  {
                    l: "CPR Bottom (BC)",
                    v: bc,
                    c: CY,
                    desc: "Bottom of CPR — institutional support zone",
                  },
                  {
                    l: "PDL (Prev Day Low)",
                    v: pdl,
                    c: GR,
                    desc: "Support — breakdown below = bearish",
                  },
                ].map(({ l, v, c, desc }) => (
                  <div
                    key={l}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      background: near(v) ? `${c}12` : "transparent",
                      borderRadius: 6,
                      marginBottom: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: c,
                        width: 44,
                        flexShrink: 0,
                      }}
                    >
                      {pct_(v) >= 0 ? "+" : ""}
                      {pct_(v)}%
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: 4,
                        background: "#1a2332",
                        borderRadius: 2,
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(
                            100,
                            50 + parseFloat(pct_(v)) * 20
                          )}%`,
                          height: "100%",
                          background: c,
                          opacity: 0.4,
                          borderRadius: 2,
                        }}
                      />
                    </div>
                    <span
                      style={mono({
                        fontSize: 13,
                        fontWeight: 700,
                        color: c,
                        width: 72,
                        textAlign: "right",
                      })}
                    >
                      ₹{fi(v)}
                    </span>
                    <span
                      style={{
                        fontSize: 9,
                        color: "#475569",
                        width: 130,
                        flexShrink: 0,
                        display: "none",
                      }}
                    >
                      {desc}
                    </span>
                    {near(v) && (
                      <span style={{ fontSize: 9, color: AM, fontWeight: 700 }}>
                        ← CMP
                      </span>
                    )}
                  </div>
                ))}
                <div style={{ marginTop: 8, fontSize: 10, color: "#475569" }}>
                  CPR Width:{" "}
                  <span
                    style={mono({
                      color: (wp / stock.cmp) * 100 < 0.4 ? GR : AM,
                    })}
                  >
                    ₹{fi(wp, 1)} ({((wp / stock.cmp) * 100).toFixed(2)}%)
                  </span>{" "}
                  ·{" "}
                  {(wp / stock.cmp) * 100 < 0.4
                    ? "Narrow → strong directional move expected"
                    : "Wide → range-bound day"}
                </div>
              </div>
            );
          })()}

          {/* Fibonacci Levels */}
          {(() => {
            const f618 = sig.fib_618,
              f382 = sig.fib_382,
              f236 = sig.fib_236,
              fext1 = sig.fib_ext1;
            if (!f618) return null;
            return (
              <div
                style={{
                  background: "#0d1117",
                  borderRadius: 12,
                  padding: "14px 16px",
                  border: "1px solid #1e2d3d",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: "#e2e8f0",
                    marginBottom: 3,
                  }}
                >
                  📐 Swing Fibonacci (20-day)
                </div>
                <div
                  style={{ fontSize: 10, color: "#475569", marginBottom: 10 }}
                >
                  Retracement from 20-day high/low swing
                </div>
                {[
                  {
                    l: "Ext 127.2%",
                    v: fext1,
                    c: "#c026d3",
                    role: "Extension target",
                  },
                  { l: "Fib 23.6%", v: f236, c: RD, role: "Resistance" },
                  {
                    l: "Fib 38.2%",
                    v: f382,
                    c: "#f97316",
                    role: "Key resistance",
                  },
                  { l: "CMP Now", v: stock.cmp, c: AM, role: "Current price" },
                  { l: "Fib 61.8%", v: f618, c: GR, role: "Key support" },
                ]
                  .filter((x) => x.v)
                  .map(({ l, v, c, role }) => (
                    <div
                      key={l}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "6px 10px",
                        background: v === stock.cmp ? `${AM}10` : "transparent",
                        borderRadius: 6,
                      }}
                    >
                      <span style={{ fontSize: 11, color: "#64748b" }}>
                        {l}
                      </span>
                      <span style={{ fontSize: 10, color: "#475569" }}>
                        {role}
                      </span>
                      <span
                        style={mono({
                          fontSize: 13,
                          fontWeight: 700,
                          color: c,
                        })}
                      >
                        ₹{fi(v)}
                      </span>
                    </div>
                  ))}
              </div>
            );
          })()}

          {/* Trade Plan */}
          <div
            style={{
              background: "#080c12",
              borderRadius: 12,
              padding: "16px 18px",
              border: `1px solid ${AM}30`,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: AM,
                marginBottom: 3,
              }}
            >
              ⚡ Trade Plan
            </div>
            <div style={{ fontSize: 11, color: "#475569", marginBottom: 12 }}>
              Anchored to PDH/PDL + CPR · Zone:{" "}
              <b style={{ color: CY }}>{sig.trade_type || "—"}</b> · ATR14: ₹
              {fi(atr14, 1)}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3,1fr)",
                gap: 10,
              }}
            >
              {[
                {
                  l: "📍 Entry",
                  v: `₹${fi(sig.entry_long ?? stock.cmp)}`,
                  sub: `vs CMP: ₹${fi(
                    Math.abs((sig.entry_long ?? stock.cmp) - stock.cmp),
                    1
                  )}`,
                  c: "#94a3b8",
                },
                {
                  l: "🎯 Target",
                  v: `₹${fi(sig.target_long ?? stock.cmp * 1.02)}`,
                  sub: `+₹${fi(
                    Math.abs(
                      (sig.target_long ?? stock.cmp * 1.02) -
                        (sig.entry_long ?? stock.cmp)
                    ),
                    1
                  )} gain`,
                  c: GR,
                },
                {
                  l: "🛑 Stop Loss",
                  v: `₹${fi(sig.sl_long ?? stock.cmp * 0.98)}`,
                  sub: `-₹${fi(
                    Math.abs(
                      (sig.entry_long ?? stock.cmp) -
                        (sig.sl_long ?? stock.cmp * 0.98)
                    ),
                    1
                  )} risk`,
                  c: RD,
                },
              ].map(({ l, v, sub, c }) => (
                <div
                  key={l}
                  style={{
                    background: "#0d1117",
                    borderRadius: 10,
                    padding: "12px 14px",
                    border: "1px solid #1e2d3d",
                  }}
                >
                  <div
                    style={{ fontSize: 10, color: "#475569", marginBottom: 5 }}
                  >
                    {l}
                  </div>
                  <div
                    style={mono({ fontSize: 18, fontWeight: 700, color: c })}
                  >
                    {v}
                  </div>
                  <div style={{ fontSize: 10, color: "#334155", marginTop: 3 }}>
                    {sub}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Win vs Lose */}
          <div
            style={{
              background: "#0d1117",
              borderRadius: 12,
              padding: "14px 16px",
              border: "1px solid #1e2d3d",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#e2e8f0",
                marginBottom: 10,
              }}
            >
              ⚖️ Win vs Lose per Lot
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                gap: 8,
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  background: "#052e16",
                  borderRadius: 9,
                  padding: 12,
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 11, color: GR, marginBottom: 3 }}>
                  If target hit
                </div>
                <div style={mono({ fontSize: 20, fontWeight: 700, color: GR })}>
                  +₹
                  {fi(
                    Math.round(
                      Math.abs(
                        (sig.target_long ?? stock.cmp * 1.02) -
                          (sig.entry_long ?? stock.cmp)
                      ) * stock.lot
                    )
                  )}
                </div>
                <div style={{ fontSize: 10, color: "#166534" }}>
                  R:R = {sig.rr_long ?? 1.6}
                </div>
              </div>
              <div style={mono({ fontSize: 18, color: "#334155" })}>vs</div>
              <div
                style={{
                  background: "#450a0a",
                  borderRadius: 9,
                  padding: 12,
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 11, color: RD, marginBottom: 3 }}>
                  If stop hit
                </div>
                <div style={mono({ fontSize: 20, fontWeight: 700, color: RD })}>
                  -₹
                  {fi(
                    Math.round(
                      Math.abs(
                        (sig.entry_long ?? stock.cmp) -
                          (sig.sl_long ?? stock.cmp * 0.98)
                      ) * stock.lot
                    )
                  )}
                </div>
                <div style={{ fontSize: 10, color: "#991b1b" }}>
                  Max loss per lot
                </div>
              </div>
            </div>
            <div
              style={{
                background: "#080c12",
                borderRadius: 9,
                padding: "10px 14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: "#e2e8f0" }}>
                  Risk : Reward
                </div>
                <div style={{ fontSize: 11, color: "#475569", marginTop: 1 }}>
                  For every ₹1 risked → ₹{sig.rr_long ?? 1.5} potential gain
                </div>
              </div>
              <div
                style={mono({
                  fontSize: 26,
                  fontWeight: 700,
                  color: (sig.rr_long ?? 1.5) >= 1.5 ? GR : AM,
                })}
              >
                1:{sig.rr_long ?? 1.5}
              </div>
            </div>
          </div>

          {/* Quick stats */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 8,
            }}
          >
            {[
              { l: "ATR14", v: `₹${fi(atr14, 1)}`, n: "Real daily range" },
              { l: "F&O Lot", v: fi(stock.lot), n: "Shares/lot" },
              {
                l: "~Margin",
                v: `₹${fi(Math.round(stock.cmp * stock.lot * 0.18))}`,
                n: "≈18% SPAN",
              },
              { l: "Sim", v: "Bootstrap", n: "Block 5-day" },
            ].map(({ l, v, n }) => (
              <div
                key={l}
                style={{
                  background: "#0d1117",
                  borderRadius: 9,
                  padding: "10px 12px",
                  border: "1px solid #1e2d3d",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 9, color: "#475569", marginBottom: 2 }}>
                  {l}
                </div>
                <div style={mono({ fontSize: 12, fontWeight: 700, color: CY })}>
                  {v}
                </div>
                <div style={{ fontSize: 9, color: "#334155", marginTop: 2 }}>
                  {n}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── OPTIONS ── */}
      {tab === "options" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div
            style={{
              background: "#052e16",
              border: `1px solid ${GR}40`,
              borderRadius: 12,
              padding: "14px 16px",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: GR,
                marginBottom: 5,
              }}
            >
              🤖 Options Strategy
            </div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: "#f1f5f9",
                marginBottom: 5,
              }}
            >
              {sig.opt_signal || "WAIT"}
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.7 }}>
              {(sig.opt_signal || "").includes("CALLS")
                ? `${(probUp * 100).toFixed(0)}% prob up + ${
                    reg.current
                  } regime. ATM strike ₹${fi(
                    sig.atm || Math.round(stock.cmp / 50) * 50
                  )}.`
                : (sig.opt_signal || "").includes("STRADDLE")
                ? "High IV — profits from big move in either direction."
                : (sig.opt_signal || "").includes("SPREAD")
                ? "Mildly bullish. Buy call + sell higher strike. Reduces cost, caps upside."
                : (sig.opt_signal || "").includes("PUTS")
                ? "Bear signal. Put profits if stock falls. Risk = premium only."
                : "Mixed signals. Wait for clearer direction before entering options."}
            </div>
          </div>
          <div
            style={{
              background: "#0d1117",
              borderRadius: 12,
              padding: "14px 16px",
              border: "1px solid #1e2d3d",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                marginBottom: 6,
              }}
            >
              <span style={{ color: "#94a3b8" }}>
                Implied Volatility (GARCH-adjusted)
              </span>
              <span
                style={mono({
                  fontWeight: 700,
                  color:
                    (sig.iv || 25) > 35 ? RD : (sig.iv || 25) > 22 ? AM : GR,
                })}
              >
                {sig.iv || 0}%
              </span>
            </div>
            <div
              style={{
                background: "#1a2332",
                borderRadius: 4,
                height: 8,
                marginBottom: 6,
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, (sig.iv || 25) * 2)}%`,
                  height: "100%",
                  borderRadius: 4,
                  background:
                    (sig.iv || 25) > 35 ? RD : (sig.iv || 25) > 22 ? AM : GR,
                }}
              />
            </div>
          </div>
          <div
            style={{
              background: "#0d1117",
              borderRadius: 12,
              padding: "14px 16px",
              border: "1px solid #1e2d3d",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#e2e8f0",
                marginBottom: 4,
              }}
            >
              📐 Greeks (30D ATM call)
            </div>
            <div style={{ fontSize: 11, color: "#475569", marginBottom: 10 }}>
              ATM ≈ ₹
              {fi(
                sig.greeks_30d?.atm ||
                  sig.atm ||
                  Math.round(stock.cmp / 50) * 50
              )}{" "}
              · Black-Scholes · r=6.7%
            </div>
            <GRow
              label="Δ Delta"
              value={(sig.greeks_30d?.delta_c ?? 0.5).toFixed(4)}
              desc="₹ move per ₹1 stock move"
            />
            <GRow
              label="Γ Gamma"
              value={(sig.greeks_30d?.gamma ?? 0).toFixed(6)}
              desc="Rate of delta change"
            />
            <GRow
              label="Θ Theta"
              value={`₹${(sig.greeks_30d?.theta_c ?? 0).toFixed(2)}/day`}
              desc="Daily time decay"
            />
            <GRow
              label="υ Vega"
              value={(sig.greeks_30d?.vega ?? 0).toFixed(4)}
              desc="Per 1% IV change"
            />
          </div>
          <div
            style={{
              background: "#0d1117",
              borderRadius: 12,
              padding: "14px 16px",
              border: "1px solid #1e2d3d",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: "#e2e8f0",
                marginBottom: 4,
              }}
            >
              💸 Call Premium by Expiry
            </div>
            {[
              { l: "30 days", g: sig.greeks_30d },
              { l: "60 days", g: sig.greeks_60d },
              { l: "90 days", g: sig.greeks_90d },
            ].map(({ l, g }) => {
              const call = g?.call ?? 0;
              return (
                <div
                  key={l}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 12px",
                    background: "#080c12",
                    borderRadius: 8,
                    marginBottom: 6,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: "#e2e8f0" }}>{l}</div>
                    <div style={{ fontSize: 10, color: "#475569" }}>
                      Black-Scholes
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={mono({
                        fontSize: 15,
                        fontWeight: 700,
                        color: "#f1f5f9",
                      })}
                    >
                      ₹{fi(call)}/share
                    </div>
                    <div
                      style={mono({ fontSize: 12, color: AM, fontWeight: 700 })}
                    >
                      ₹{fi(Math.round(call * stock.lot))} · 1 lot
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   ROOT APP
══════════════════════════════════════════════════════════════════ */
export default function App() {
  const [model, setModel] = useState(() => BASE_MODEL);
  const [selSym, setSelSym] = useState(() => BASE_MODEL[0].symbol);
  const [search, setSearch] = useState(""),
    [sector, setSector] = useState("All");
  const [sortBy, setSortBy] = useState("score"),
    [isReal, setIsReal] = useState(false);
  const fileRef = useRef();

  const sel = useMemo(
    () => model.find((m) => m.symbol === selSym) || model[0],
    [model, selSym]
  );
  const sectors = useMemo(
    () => ["All", ...new Set(model.map((m) => m.sector))].sort(),
    [model]
  );

  const filtered = useMemo(() => {
    let d = model.filter(
      (m) =>
        (sector === "All" || m.sector === sector) &&
        (search === "" ||
          m.symbol.toLowerCase().includes(search.toLowerCase()) ||
          m.name.toLowerCase().includes(search.toLowerCase()))
    );
    if (sortBy === "score")
      return [...d].sort(
        (a, b) => (b.signals?.score || 0) - (a.signals?.score || 0)
      );
    if (sortBy === "upside")
      return [...d].sort((a, b) => (b.target6m || 0) - (a.target6m || 0));
    if (sortBy === "price") return [...d].sort((a, b) => a.cmp - b.cmp);
    return [...d].sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [model, sector, search, sortBy]);

  const buyCount = filtered.filter((s) => (s.signals?.score || 0) >= 60).length;

  const onUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const rd = new FileReader();
    rd.onload = (ev) => {
      try {
        const j = JSON.parse(ev.target.result);
        const arr = j.stocks || j;
        if (!Array.isArray(arr)) {
          alert("Need mcmc_results.json from the Python script.");
          return;
        }
        const enriched = enrichFromJSON(arr);
        setModel(enriched);
        setIsReal(true);
        setSelSym(enriched[0]?.symbol || "");
      } catch (ex) {
        alert("Could not parse JSON. Ensure it's mcmc_results.json.\n" + ex);
      }
    };
    rd.readAsText(file);
    e.target.value = "";
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "#080c12",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#0d1117",
          borderBottom: "1px solid #1e2d3d",
          padding: "0 20px",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 56,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "linear-gradient(135deg,#1d4ed8,#7c3aed)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
              }}
            >
              📈
            </div>
            <div>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 15,
                  color: "#f1f5f9",
                  letterSpacing: -0.3,
                }}
              >
                NSE MCMC Dashboard
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: "#475569",
                  fontFamily: "'Playfair Display',serif",
                  fontStyle: "italic",
                }}
              >
                History maps the future — MCMC + Bootstrap + CPR ·{" "}
                {model.length} stocks
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            {[
              { v: model.length, l: "STOCKS", c: AM },
              { v: buyCount, l: "BUY+", c: GR },
              {
                v: model.filter((s) => (s.signals?.score || 0) < 30).length,
                l: "AVOID",
                c: RD,
              },
            ].map(({ v, l, c }, i) => (
              <React.Fragment key={l}>
                {i > 0 && (
                  <div
                    style={{ width: 1, height: 30, background: "#1e2d3d" }}
                  />
                )}
                <div style={{ textAlign: "center" }}>
                  <div
                    style={mono({ fontSize: 20, fontWeight: 700, color: c })}
                  >
                    {v}
                  </div>
                  <div style={{ fontSize: 9, color: "#475569" }}>{l}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isReal ? (
              <span
                style={{
                  background: "#052e16",
                  color: GR,
                  border: `1px solid ${GR}40`,
                  padding: "4px 12px",
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                ✓ Real yfinance Data
              </span>
            ) : (
              <span
                style={{
                  background: "#422006",
                  color: AM,
                  border: `1px solid ${AM}40`,
                  padding: "4px 12px",
                  borderRadius: 20,
                  fontSize: 11,
                }}
              >
                ◌ Preview Mode
              </span>
            )}
            <input
              type="file"
              ref={fileRef}
              accept=".json"
              onChange={onUpload}
              style={{ display: "none" }}
            />
            <button
              onClick={() => fileRef.current.click()}
              style={{
                background: "linear-gradient(135deg,#1d4ed8,#7c3aed)",
                color: "#fff",
                border: "none",
                borderRadius: 9,
                padding: "8px 16px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.target.style.opacity = ".85")}
              onMouseLeave={(e) => (e.target.style.opacity = "1")}
            >
              ⬆ Load Real Data
            </button>
          </div>
        </div>
        <div
          style={{
            borderTop: "1px solid #111827",
            padding: "5px 0",
            display: "flex",
            gap: 20,
            overflowX: "auto",
            scrollbarWidth: "none",
          }}
        >
          {[
            "📖 2Y NSE data",
            "⚙️ MCMC Bayesian",
            "🌊 GARCH(1,1) clustering",
            "🔀 Markov Regime Switching",
            "🎲 Block Bootstrap (no GBM)",
            "🎯 CPR + PDH/PDL + Swing Fib anchored trade levels",
          ].map((t) => (
            <span
              key={t}
              style={{
                fontSize: 10,
                color: "#334155",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Main */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "320px 1fr",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* List */}
        <div
          style={{
            background: "#0a0f18",
            borderRight: "1px solid #1e2d3d",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "10px", borderBottom: "1px solid #111827" }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍  Search symbol or name…"
              style={{
                width: "100%",
                padding: "7px 10px",
                borderRadius: 8,
                border: "1px solid #1e2d3d",
                fontSize: 12,
                color: "#e2e8f0",
                background: "#0d1117",
                outline: "none",
                marginBottom: 7,
              }}
            />
            <select
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              style={{
                width: "100%",
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #1e2d3d",
                fontSize: 11,
                color: "#94a3b8",
                background: "#0d1117",
                marginBottom: 7,
                cursor: "pointer",
                outline: "none",
              }}
            >
              {sectors.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <div style={{ display: "flex", gap: 4 }}>
              {[
                ["score", "⭐ Score"],
                ["upside", "📈 Upside"],
                ["price", "₹ Price"],
                ["name", "A–Z"],
              ].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setSortBy(k)}
                  style={{
                    flex: 1,
                    padding: "5px 0",
                    borderRadius: 6,
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: "pointer",
                    border: `1px solid ${sortBy === k ? "#1e3a5f" : "#1e2d3d"}`,
                    background: sortBy === k ? "#0d1f33" : "transparent",
                    color: sortBy === k ? CY : "#475569",
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div
            style={{
              padding: "4px 10px",
              background: "#080c12",
              borderBottom: "1px solid #111827",
              display: "flex",
              gap: 10,
              fontSize: 10,
              color: "#334155",
            }}
          >
            <span>
              Showing <b style={{ color: "#94a3b8" }}>{filtered.length}</b>
            </span>
            <span>
              · Buy+ <b style={{ color: GR }}>{buyCount}</b>
            </span>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "4px 6px" }}>
            {filtered.map((s) => (
              <StockRow
                key={s.symbol}
                stock={s}
                selected={s.symbol === selSym}
                onClick={() => setSelSym(s.symbol)}
              />
            ))}
            {filtered.length === 0 && (
              <div
                style={{ textAlign: "center", padding: 40, color: "#334155" }}
              >
                No stocks found
              </div>
            )}
          </div>
        </div>
        {/* Detail */}
        <DetailPanel stock={sel} />
      </div>
    </div>
  );
}
